/**
 * At-Least-One-When Validation Service
 *
 * Validates that when a trigger field has a specific value, at least one of a set of
 * dependent fields must have a required value (e.g. "If Anti-TNFa = Yes, at least one
 * of Infliximab, Adalimumab or Certolizumab must be Yes").
 *
 * Configuration is section-level: atLeastOneRequiredWhen array on the section config.
 * Errors are set on the trigger field so the user sees the message on the controlling field.
 *
 * @see DYNAMIC_FORM_ENGINE_DOCUMENTATION.md (optional: document atLeastOneRequiredWhen)
 */

import { getIn } from 'formik';

const DEFAULT_MESSAGE =
  'At least one of the options below must be selected when this field has the selected value.';

/**
 * Set a nested error by dot path (e.g. "categoryKey.fieldKey" -> errors[categoryKey][fieldKey] = message).
 * Mutates the errors object.
 *
 * @param {Object} errors - Errors object to mutate (Formik-shaped)
 * @param {string} fieldPath - Dot-separated path (e.g. "prior_advanced_therapy_Category.Anti-TNFa")
 * @param {string} message - Error message
 */
const setErrorByPath = (errors, fieldPath, message) => {
  if (!fieldPath || !message) return;
  const pathParts = fieldPath.split('.');
  if (pathParts.length >= 2) {
    const [categoryKey, ...rest] = pathParts;
    const fieldKey = rest.join('.');
    if (!errors[categoryKey]) {
      errors[categoryKey] = {};
    }
    errors[categoryKey][fieldKey] = message;
  } else {
    errors[fieldPath] = message;
  }
};

/**
 * Validates "at least one of dependent fields must equal requiredValue when trigger equals triggerValue".
 *
 * @param {Object} config - Section or form config that may contain atLeastOneRequiredWhen (array of rules).
 * @param {Object} formValues - Current form values (nested by category).
 * @param {Object} context - Optional. { userSiteTrialRole } for future access checks.
 * @returns {{
 *   isValid: boolean,
 *   errors: Object,
 *   failedRules: Array<{ rule: Object, triggerPath: string }>
 * }}
 */
export const validateAtLeastOneWhen = (config, formValues, context = {}) => {
  const errors = {};
  const failedRules = [];

  if (!config || !formValues) {
    return { isValid: true, errors: {}, failedRules: [] };
  }

  const rules = config.atLeastOneRequiredWhen;
  if (!Array.isArray(rules) || rules.length === 0) {
    return { isValid: true, errors: {}, failedRules: [] };
  }

  for (const rule of rules) {
    if (!rule || typeof rule.triggerField !== 'string' || !Array.isArray(rule.dependentFields)) {
      continue;
    }

    const triggerValue = getIn(formValues, rule.triggerField);
    const requiredTrigger = rule.triggerValue;
    const requiredValue =
      rule.requiredValue !== undefined && rule.requiredValue !== null
        ? rule.requiredValue
        : requiredTrigger;

    if (triggerValue !== requiredTrigger) {
      continue;
    }

    const atLeastOneMatch = rule.dependentFields.some((path) => {
      const value = getIn(formValues, path);
      return value === requiredValue;
    });

    if (!atLeastOneMatch) {
      failedRules.push({ rule, triggerPath: rule.triggerField });
      const message =
        typeof rule.message === 'string' && rule.message.trim() !== ''
          ? rule.message.trim()
          : DEFAULT_MESSAGE;
      setErrorByPath(errors, rule.triggerField, message);
    }
  }

  const isValid = failedRules.length === 0;

  if (!isValid) {
    console.warn('[atLeastOneValidationService] validateAtLeastOneWhen: Validation failed', {
      failedRulesCount: failedRules.length,
      triggerPaths: failedRules.map((f) => f.triggerPath),
    });
  }

  return { isValid, errors, failedRules };
};
